import { TableColumn } from '@arnold/common';
import styled from '@emotion/styled';
import { TFunction } from 'react-i18next';
import {
  NotstartedSurveyReason,
  OrganizationQuery,
  ProcessStatus,
  SurveyGroupRespondentsQuery,
  SurveyStatus,
  UserQuery,
} from '../../../generated/hooks';
import { SurveyInfo } from '../../SurveysOverview/RowParts';
import { ProcessSurveyParticipantsData } from './types';

const NotStartedStatus = styled.div`
  display: flex;
  gap: 8px;
`;

export const getColumns = (
  t: TFunction,
  isOnboarding?: boolean,
  isHiring?: boolean,
): Array<TableColumn<ProcessSurveyParticipantsData>> =>
  [
    {
      key: 'surveyParticipant',
      title: t('surveyParticipant'),
      dataIndex: 'surveyParticipant',
    },
    {
      key: 'startDate',
      title: t('startDate'),
      dataIndex: 'startDate',
    },
    {
      key: 'editDate',
      title: undefined,
      dataIndex: 'editDate',
    },
    isHiring && {
      key: 'recruiter',
      title: t('recruiter'),
      dataIndex: 'recruiter',
    },
    {
      key: 'team',
      title: t('team'),
      dataIndex: 'team',
    },
    {
      key: 'status',
      title: isOnboarding ? t('status') : t('surveyGroupStatus'),
      dataIndex: 'status',
    },
    {
      key: 'displayAnswers',
      title: undefined,
      dataIndex: 'displayAnswers',
    },
    {
      key: 'deleteButton',
      title: undefined,
      dataIndex: 'deleteButton',
    },
  ].filter(Boolean) as Array<TableColumn<ProcessSurveyParticipantsData>>;

export const RESPONDENTS_ON_PAGE = 15;

export const getParticipantStatus = (
  surveys: SurveyGroupRespondentsQuery['surveyGroupRespondents']['data'][0]['surveys'],
  inLicenceInterval: boolean,
  organization: UserQuery['user']['organization'] | OrganizationQuery['organization'],
  status: ProcessStatus,
  t: TFunction,
  notstartedReason?: NotstartedSurveyReason[],
  isDeleted?: boolean,
  isCreatingSurveys?: boolean,
) => {
  if (isCreatingSurveys) {
    return t('preparing');
  }
  if (isDeleted) {
    return t('deleted');
  }
  if (surveys.every((survey) => survey.status === SurveyStatus.Prepared) && surveys.length > 0) {
    return t('scheduled');
  }

  if (surveys.every((survey) => [SurveyStatus.Completed, SurveyStatus.Archived].includes(survey.status))) {
    return t('completed');
  }

  if (surveys.every((survey) => survey.status === SurveyStatus.Notstarted)) {
    return (
      <NotStartedStatus>
        {t('notstarted')}
        <SurveyInfo
          isSequential={true}
          inLicenceInterval={inLicenceInterval}
          organization={organization}
          status={status}
          notstartedReason={notstartedReason}
        />
      </NotStartedStatus>
    );
  }

  if (
    surveys.find(
      (survey) =>
        survey.status === SurveyStatus.Notstarted &&
        survey.notstartedReason !== NotstartedSurveyReason.NoRespondentsInSg,
    )
  ) {
    return (
      <NotStartedStatus>
        {t('partiallyNotstarted')}
        <SurveyInfo
          isSequential={true}
          inLicenceInterval={inLicenceInterval}
          organization={organization}
          status={status}
          notstartedReason={notstartedReason}
        />
      </NotStartedStatus>
    );
  }

  return t('ongoing');
};
